import Caret from '@clearkit/icons/glyphs/Caret';
import GoExternal from '@clearkit/icons/glyphs/GoExternal';
import Hubspot from '@clearkit/icons/glyphs/Hubspot';
import Salesforce from '@clearkit/icons/glyphs/Salesforce';
import {
  CKActionMenu,
  CKButton,
  CKCompanyLogo,
  CKPopover,
} from '@clearkit/react';

import { dollars } from '~/lib/formatters';

import styles from './styles';
import TwoLineMenuItem from './TwoLineMenuItem';

const Actions = ({
  classes = {},
  hubspotOwners,
  hubspotCompanies,
  salesforceAccounts,
  salesforceAccountConnector,
}) => {
  return (
    <div className="flex flex-row">
      {salesforceAccounts?.length > 0 && (
        <>
          <CKPopover
            className="z-10 overflow-y-auto popover-max-height"
            aria-label="Popover"
            options={{ placement: 'bottom-end', gutter: 4 }}
            popover={
              <CKActionMenu
                aria-label="Default Menu"
                hideOnClickOutside={false}
                visible>
                {salesforceAccounts?.map((salesforceAccount) => {
                  return (
                    <div key={salesforceAccount.externalId}>
                      <span className="px-3 pt-2 text-sm font-medium text-gray">
                        Account &mdash; {salesforceAccount.name}
                      </span>
                      <CKActionMenu.Item
                        as="a"
                        href={`${salesforceAccountConnector.externalInstanceUrl}/${salesforceAccount.externalId}`}
                        target="blank"
                        rel="noopener noreferrer"
                        key={salesforceAccount.externalId}
                        icon={
                          <CKCompanyLogo
                            domain={salesforceAccount.websiteDomain}
                            size={32}
                          />
                        }>
                        <span className="flex flex-row items-center flex-grow">
                          <TwoLineMenuItem
                            title={salesforceAccount.name}
                            subtitle={
                              <>
                                Owner &mdash;{' '}
                                {salesforceAccount?.owner?.name ||
                                  'Unknown User'}
                              </>
                            }
                          />
                          <GoExternal className="ml-auto text-blue-500" />
                        </span>
                      </CKActionMenu.Item>
                      {salesforceAccount?.opportunities?.length > 0 && (
                        <span className="px-3 pt-2 text-sm font-medium text-gray">
                          {salesforceAccount.opportunities?.length}{' '}
                          {salesforceAccount.opportunities?.length == 1
                            ? 'opportunity'
                            : 'opportunities'}
                        </span>
                      )}
                      {salesforceAccount?.opportunities?.map((opportunity) => {
                        return (
                          <CKActionMenu.Item
                            key={opportunity.externalId}
                            as="a"
                            href={`${salesforceAccountConnector.externalInstanceUrl}/${opportunity.externalId}`}
                            target="blank"
                            rel="noopener noreferrer">
                            <span className="flex flex-row items-center flex-grow">
                              <TwoLineMenuItem
                                title={opportunity.name}
                                subtitle={
                                  <>
                                    Owner &mdash;{' '}
                                    {opportunity.owner?.name || 'Unknown User'}{' '}
                                    - {dollars(opportunity.amount)}
                                  </>
                                }
                              />
                              <GoExternal className="ml-auto text-blue-500" />
                            </span>
                          </CKActionMenu.Item>
                        );
                      })}
                      {salesforceAccount !==
                        salesforceAccounts[salesforceAccounts?.length - 1] && (
                        <CKActionMenu.Divider />
                      )}
                    </div>
                  );
                })}
              </CKActionMenu>
            }>
            <CKButton
              className={classnames(classes.salesforceButton, 'ml-4')}
              leftIcon={<Salesforce className="mr-1" />}
              rightIcon={<Caret className="-mr-1" />}>
              Salesforce
            </CKButton>
          </CKPopover>
        </>
      )}
      {hubspotCompanies?.length > 0 && (
        <>
          <CKPopover
            className="z-10 overflow-y-auto popover-max-height"
            aria-label="Popover"
            options={{ placement: 'bottom-end', gutter: 4 }}
            popover={
              <CKActionMenu
                aria-label="Default Menu"
                hideOnClickOutside={false}
                visible>
                {hubspotCompanies?.map((hubspotCompany) => (
                  <div key={hubspotCompany.id}>
                    <span className="px-3 pt-2 text-sm font-medium text-gray">
                      Account &mdash; {hubspotCompany.name}
                    </span>
                    <CKActionMenu.Item
                      as="a"
                      href={`https://app.hubspot.com/contacts/${hubspotCompany.externalAccountId}/company/${hubspotCompany.id}`}
                      target="blank"
                      rel="noopener noreferrer"
                      key={hubspotCompany.id}
                      icon={
                        <CKCompanyLogo
                          domain={hubspotCompany.domain}
                          size={32}
                        />
                      }>
                      <span className="flex flex-row items-center flex-grow">
                        <TwoLineMenuItem title={hubspotCompany.name} />
                        <GoExternal className="ml-auto text-blue-500" />
                      </span>
                    </CKActionMenu.Item>
                    {hubspotCompany.deals?.length > 0 && (
                      <span className="px-3 pt-2 text-sm font-medium text-gray">
                        {hubspotCompany.deals?.length}{' '}
                        {hubspotCompany.deals?.length == 1 ? 'deal' : 'deals'}
                      </span>
                    )}
                    {hubspotCompany?.deals?.map((deal) => {
                      const owner = hubspotOwners?.find(
                        (owner) => owner.id === deal.ownerId,
                      );
                      const ownerName = owner?.ownerName || 'Unknown User';

                      return (
                        <CKActionMenu.Item
                          key={deal.id}
                          as="a"
                          href={`https://app.hubspot.com/contacts/${
                            hubspotCompany.externalAccountId
                          }/deal/${deal['id']}`}
                          target="blank"
                          rel="noopener noreferrer">
                          <span className="flex flex-row items-center flex-grow">
                            <TwoLineMenuItem
                              title={deal.name}
                              subtitle={
                                <>
                                  {owner && <>Owner: {ownerName}</>}
                                  <>
                                    {deal.amount && (
                                      <> &mdash; {dollars(deal.amount)}</>
                                    )}
                                  </>
                                </>
                              }
                            />
                            <GoExternal className="ml-auto text-blue-500" />
                          </span>
                        </CKActionMenu.Item>
                      );
                    })}

                    {hubspotCompany !==
                      hubspotCompanies[hubspotCompanies?.length - 1] && (
                      <CKActionMenu.Divider />
                    )}
                  </div>
                ))}
              </CKActionMenu>
            }>
            <CKButton
              className={classnames(classes.hubspotButton, 'ml-4')}
              leftIcon={<Hubspot className="mr-1" />}
              rightIcon={<Caret className="-mr-1" />}>
              HubSpot
            </CKButton>
          </CKPopover>
        </>
      )}
    </div>
  );
};

Actions.propTypes = {
  classes: PropTypes.object,
  hubspotCompanies: PropTypes.array,
  salesforceAccounts: PropTypes.array,
  hubspot: PropTypes.object,
  salesforce: PropTypes.object,
  hubspotOwners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  ),
  salesforceAccountConnector: PropTypes.shape({
    externalInstanceUrl: PropTypes.string,
  }),
};

export default withStyles(styles)(Actions);
